import React from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Grid, MenuItem, Tooltip, Icon, Modal, Avatar } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import FormField from "components/FormField";
import { useSnackbar } from "notistack";
import { useMaterialUIController } from "context";

import form from "../../pages/new-student/schemas/form";
import initialValues from "../../pages/new-student/schemas/initialValues";
import validations from "../../pages/new-student/schemas/validations";

import { useGetGeneroQuery, useGetCarrerasQuery } from "services";
import { useStudentRole, useStudentFetch, useStudentSubmit } from "./hooks";
import { ESTADOS_ESTUDIANTES, TIPOS_SANGRES } from "helpers/global_vars";

import Telefonos from "examples/TelefonosCRUD/telefonos";

const NewStudent = ({ open, handleClose, id }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { formId, formField } = form;
  const {
    nombre,
    apellido_paterno,
    apellido_materno,
    curp,
    genero,
    tipo_sangre,
    numero_de_control,
    estado,
    preasignacion_de_carrera,
    usuario_wifi,
    password_wifi,
    groups,
  } = formField;

  const { data: generos } = useGetGeneroQuery();
  const { data: carreras } = useGetCarrerasQuery({
    filter_params: {
      query: "{id,url,nombre,plan_de_estudio}",
      ordering: "nombre,plan_de_estudio",
    },
    shape: {
      id: "id",
      nombre: "nombre",
      plan_de_estudio: "plan_de_estudio",
      url: "url",
    },
  });
  const { enqueueSnackbar } = useSnackbar();

  const currentValidation = validations.reduce(
    (acc, curr) => acc.concat(curr),
    Yup.object(),
  );

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: 900,
    boxShadow: 24,
    borderRadius: "12px",
    px: "40px",
    py: "29px",
    outline: "none",
    maxHeight: "95vh",
    overflowY: "auto",
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <MDBox sx={modalStyle} bgColor={darkMode ? "black" : "white"}>
        <Formik
          initialValues={initialValues}
          validationSchema={currentValidation}
          onSubmit={() => {}} // Placeholder, will be handled inside via hook
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
            isSubmitting,
            setErrors,
            setSubmitting,
            resetForm,
          }) => {
            useStudentRole(setFieldValue, groups.name);

            const {
              oldValues,
              estado: fetchedEstado,
              estadoIdState,
              getEstudianteById,
            } = useStudentFetch(id, setFieldValue, formField);

            const { handleSubmit } = useStudentSubmit(
              id,
              handleClose,
              oldValues,
              fetchedEstado,
              estadoIdState,
              getEstudianteById,
            );

            return (
              <Form
                id={formId}
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(values, { setSubmitting, resetForm, setErrors });
                }}
              >
                <Grid container columnSpacing={2}>
                  <Grid item xs={12} display="flex" alignItems="flex-start">
                    <Grid container columnSpacing={2}>
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                      >
                        <Tooltip title="Cambiar Foto" placement="top">
                          <MDBox position="relative" display="inline-flex">
                            <input
                              accept="image/*"
                              style={{ display: "none" }}
                              id="student-image-upload"
                              type="file"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  if (file.size > 1024 * 1024) {
                                    enqueueSnackbar(
                                      "La imagen debe ser menor a 1MB",
                                      { variant: "error" },
                                    );
                                  } else {
                                    setFieldValue("foto", file);
                                  }
                                }
                              }}
                            />
                            <label
                              htmlFor="student-image-upload"
                              style={{ cursor: "pointer" }}
                            >
                              <Avatar
                                src={
                                  values.foto
                                    ? typeof values.foto === "string"
                                      ? values.foto
                                      : URL.createObjectURL(values.foto)
                                    : undefined
                                }
                                sx={{
                                  width: 150,
                                  height: 150,
                                  bgcolor: values.foto
                                    ? "transparent"
                                    : "#e0e0e0",
                                }}
                              >
                                <Icon
                                  sx={{
                                    width: 60,
                                    height: 60,
                                    fontSize: "60px !important",
                                    color: "#9e9e9e",
                                  }}
                                >
                                  person
                                </Icon>
                              </Avatar>
                            </label>

                            <MDBox
                              component="label"
                              htmlFor="student-image-upload"
                              position="absolute"
                              bottom={0}
                              right={10}
                              sx={{
                                bgcolor: "#3784A1",
                                borderRadius: "50%",
                                p: 1,
                                cursor: "pointer",
                                border: "2px solid white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                zIndex: 2,
                              }}
                            >
                              <Icon sx={{ color: "white" }}>camera_alt</Icon>
                            </MDBox>
                          </MDBox>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <MDBox mb={1}>
                          <FormField
                            name={nombre.name}
                            label={nombre.label}
                            value={values.nombre}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.nombre && touched.nombre}
                            success={values.nombre.length > 0 && !errors.nombre}
                            onInput={(e) =>
                              (e.target.value = (
                                "" + e.target.value
                              ).toUpperCase())
                            }
                            noMarginBottom
                          />
                        </MDBox>
                        <MDBox mb={1}>
                          <FormField
                            name={apellido_paterno.name}
                            label={apellido_paterno.label}
                            value={values.apellido_paterno}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              errors.apellido_paterno &&
                              touched.apellido_paterno
                            }
                            success={
                              values.apellido_paterno.length > 0 &&
                              !errors.apellido_paterno
                            }
                            onInput={(e) =>
                              (e.target.value = (
                                "" + e.target.value
                              ).toUpperCase())
                            }
                            noMarginBottom
                          />
                        </MDBox>
                        <MDBox mb={1}>
                          <FormField
                            name={apellido_materno.name}
                            label={apellido_materno.label}
                            value={values.apellido_materno}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              errors.apellido_materno &&
                              touched.apellido_materno
                            }
                            success={
                              values.apellido_materno.length > 0 &&
                              !errors.apellido_materno
                            }
                            onInput={(e) =>
                              (e.target.value = (
                                "" + e.target.value
                              ).toUpperCase())
                            }
                            noMarginBottom
                          />
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormField
                      name={curp.name}
                      label={curp.label}
                      value={values.curp}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.curp && touched.curp}
                      success={values.curp.length > 0 && !errors.curp}
                      onInput={(e) =>
                        (e.target.value = ("" + e.target.value)
                          .replace(/\s/g, "")
                          .toUpperCase())
                      }
                      noMarginBottom
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      name={numero_de_control.name}
                      label={numero_de_control.label}
                      value={values.numero_de_control}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        errors.numero_de_control && touched.numero_de_control
                      }
                      success={
                        values.numero_de_control.length > 0 &&
                        !errors.numero_de_control
                      }
                      onInput={(e) =>
                        (e.target.value = ("" + e.target.value)
                          .replace(/\s/g, "")
                          .toUpperCase())
                      }
                      noMarginBottom
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormField
                      select
                      name={genero.name}
                      label={genero.label}
                      value={values.genero}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.genero && touched.genero}
                      success={values.genero.length > 0 && !errors.genero}
                      noMarginBottom
                    >
                      <MenuItem value="">No Seleccionado</MenuItem>
                      {generos?.map((g) => (
                        <MenuItem key={g.id_genero} value={g.url}>
                          {g.genero}
                        </MenuItem>
                      ))}
                    </FormField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormField
                      select
                      name={tipo_sangre.name}
                      label={tipo_sangre.label}
                      value={values.tipo_sangre}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.tipo_sangre && touched.tipo_sangre}
                      success={
                        values.tipo_sangre?.length > 0 && !errors.tipo_sangre
                      }
                      noMarginBottom
                    >
                      <MenuItem value="">No Seleccionado</MenuItem>
                      {TIPOS_SANGRES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </FormField>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormField
                      select
                      name={estado.name}
                      label={estado.label}
                      value={values.estado}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={errors.estado && touched.estado}
                      success={values.estado?.length > 0 && !errors.estado}
                      noMarginBottom
                    >
                      <MenuItem value="">No Seleccionado</MenuItem>
                      {ESTADOS_ESTUDIANTES.map((item) => (
                        <MenuItem key={item} value={item}>
                          {item}
                        </MenuItem>
                      ))}
                    </FormField>
                  </Grid>

                  <Grid item xs={12}>
                    <FormField
                      select
                      name={preasignacion_de_carrera.name}
                      label={preasignacion_de_carrera.label}
                      value={values.preasignacion_de_carrera}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        errors.preasignacion_de_carrera &&
                        touched.preasignacion_de_carrera
                      }
                      success={
                        values.preasignacion_de_carrera.length > 0 &&
                        !errors.preasignacion_de_carrera
                      }
                      noMarginBottom
                    >
                      <MenuItem value="">No Seleccionado</MenuItem>
                      {carreras?.map((c) => (
                        <MenuItem key={c.id} value={c.url}>
                          {c.nombre} {c.plan_de_estudio}
                        </MenuItem>
                      ))}
                    </FormField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormField
                      name={usuario_wifi.name}
                      label={usuario_wifi.label}
                      value={values.usuario_wifi}
                      onChange={handleChange}
                      onInput={(e) =>
                        (e.target.value = ("" + e.target.value)
                          .replace(/\s/g, "")
                          .toUpperCase())
                      }
                      noMarginBottom
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormField
                      name={password_wifi.name}
                      label={password_wifi.label}
                      value={values.password_wifi}
                      onChange={handleChange}
                      onInput={(e) =>
                        (e.target.value = ("" + e.target.value)
                          .replace(/\s/g, "")
                          .toUpperCase())
                      }
                      noMarginBottom
                    />
                  </Grid>

                  <Grid item xs={12} mt={2}>
                    <Telefonos
                      formData={{
                        values,
                        touched,
                        formField,
                        errors,
                        setErrors,
                        setFieldValue,
                        handleChange,
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="center"
                    gap={2}
                    mt={2}
                  >
                    <MDButton
                      variant="contained"
                      color="accent"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      Aceptar
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="accent"
                      onClick={handleClose}
                    >
                      Cancelar
                    </MDButton>
                  </Grid>
                </Grid>
              </Form>
            );
          }}
        </Formik>
      </MDBox>
    </Modal>
  );
};

NewStudent.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  id: PropTypes.string,
};

export default NewStudent;
